import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usersApi, adminApi } from '../utils/api'
import { t } from '../utils/translations'
import { useLocation } from 'react-router-dom'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const ROLES = [
  { value: 'viewer', label: { en: 'View-only', zh: '唯讀用戶' } },
  { value: 'user', label: { en: 'User', zh: '一般用戶' } },
  { value: 'admin', label: { en: 'Admin', zh: '管理員' } },
  { value: 'sysadmin', label: { en: 'System Admin', zh: '系統管理員' } }
]

export default function SysAdminPage({ lang = 'zh' }) {
  const location = useLocation()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState(
    location.pathname === '/sysadmin/user' ? 'users' : 'overview'
  )
  const [users, setUsers] = useState([])
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceLoading, setMaintenanceLoading] = useState(false)

  // Notes state
  const [notes, setNotes] = useState([])
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [noteForm, setNoteForm] = useState({ title: '', description: '' })
  const [noteCategory, setNoteCategory] = useState('feature') // 'feature', 'note', 'vibe', 'milestone'

  // Documents state
  const [documents, setDocuments] = useState([])
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [documentContent, setDocumentContent] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)

  // User form state
  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    role: 'user',
    churches: []
  })
  const [passwordUnlocked, setPasswordUnlocked] = useState(false)
  const [showPasswordText, setShowPasswordText] = useState(false)
  const [sysadminPasswordInput, setSysadminPasswordInput] = useState('')
  const [passwordUnlockError, setPasswordUnlockError] = useState('')
  const [passwordUnlockLoading, setPasswordUnlockLoading] = useState(false)

  // Log filters
  const [logFilters, setLogFilters] = useState({
    userId: '',
    action: '',
    limit: 100
  })

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers()
    } else if (activeTab === 'logs') {
      loadLogs()
    } else if (activeTab === 'notes') {
      loadNotes(noteCategory)
    } else if (activeTab === 'documents') {
      loadDocuments()
    } else if (activeTab === 'overview') {
      loadUsers()
      loadMaintenanceStatus()
    }
  }, [activeTab, noteCategory])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await usersApi.list()
      setUsers(response.data.data || [])
    } catch (err) {
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const loadLogs = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getLogs(logFilters)
      setLogs(response.data.data.logs || [])
    } catch (err) {
      setError('Failed to load logs')
    } finally {
      setLoading(false)
    }
  }

  const loadMaintenanceStatus = async () => {
    try {
      const response = await adminApi.getMaintenance()
      setMaintenanceMode(response.data.data.maintenanceMode)
    } catch (err) {
      console.error('Failed to load maintenance status:', err)
    }
  }

  const toggleMaintenance = async () => {
    try {
      setMaintenanceLoading(true)
      await adminApi.setMaintenance(!maintenanceMode)
      setMaintenanceMode(!maintenanceMode)
      setSuccess(!maintenanceMode
        ? (lang === 'zh' ? '系統維護模式已啟用' : 'Maintenance mode enabled')
        : (lang === 'zh' ? '系統維護模式已停用' : 'Maintenance mode disabled')
      )
    } catch (err) {
      setError('Failed to update maintenance mode')
    } finally {
      setMaintenanceLoading(false)
    }
  }

  const loadNotes = async (category = null) => {
    try {
      setLoading(true)
      console.log('loadNotes called with category:', category)
      const response = await adminApi.getNotes(category)
      console.log('getNotes response:', response.data)
      setNotes(response.data.data || [])
    } catch (err) {
      setError('Failed to load notes')
    } finally {
      setLoading(false)
    }
  }

  const handleNoteSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingNote) {
        await adminApi.updateNote(editingNote.note_id, {
          title: noteForm.title,
          description: noteForm.description,
          completed: editingNote.completed
        })
        setSuccess(lang === 'zh' ? '筆記已更新' : 'Note updated')
      } else {
        console.log('Creating note with category:', noteCategory)
        await adminApi.createNote(noteForm.title, noteForm.description, noteCategory)
        setSuccess(lang === 'zh' ? '筆記已建立' : 'Note created')
      }
      setShowNoteForm(false)
      setEditingNote(null)
      setNoteForm({ title: '', description: '' })
      loadNotes(noteCategory)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save note')
    }
  }

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(lang === 'zh' ? '確定要刪除此筆記？' : 'Delete this note?')) return

    try {
      await adminApi.deleteNote(noteId)
      setSuccess(lang === 'zh' ? '筆記已刪除' : 'Note deleted')
      loadNotes(noteCategory)
    } catch (err) {
      setError('Failed to delete note')
    }
  }

  const toggleNoteComplete = async (note) => {
    try {
      await adminApi.updateNote(note.note_id, { completed: !note.completed })
      loadNotes(noteCategory)
    } catch (err) {
      setError('Failed to update note')
    }
  }

  const openEditNote = (note) => {
    setEditingNote(note)
    setNoteForm({ title: note.title, description: note.description || '' })
    setShowNoteForm(true)
  }

  // Documents functions
  const loadDocuments = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getDocuments()
      setDocuments(response.data.data || [])
    } catch (err) {
      setError('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async () => {
    if (!uploadFiles || uploadFiles.length === 0) return

    try {
      setLoading(true)
      setUploadProgress(0)

      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i]

        // Get presigned URL
        const response = await adminApi.getDocumentUploadUrl(file.name)
        const { uploadUrl, key } = response.data.data

        // Upload to S3
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': 'text/markdown'
          }
        })

        setUploadProgress(Math.round(((i + 1) / uploadFiles.length) * 100))
      }

      setSuccess(lang === 'zh' ? `已上傳 ${uploadFiles.length} 個文件` : `${uploadFiles.length} file(s) uploaded`)
      setShowUploadModal(false)
      setUploadFiles([])
      setUploadProgress(0)
      loadDocuments()
    } catch (err) {
      setError('Failed to upload document')
    } finally {
      setLoading(false)
    }
  }

  const handleViewDocument = async (doc) => {
    try {
      setLoading(true)
      const response = await adminApi.getDocumentDownloadUrl(doc.key)
      const downloadUrl = response.data.data.downloadUrl

      // Fetch the content
      const contentResponse = await fetch(downloadUrl)
      const content = await contentResponse.text()

      setSelectedDocument(doc)
      setDocumentContent(content)
    } catch (err) {
      setError('Failed to load document')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(lang === 'zh' ? '確定要刪除此文件？' : 'Delete this document?')) return

    try {
      await adminApi.deleteDocument(doc.key)
      setSuccess(lang === 'zh' ? '文件已刪除' : 'Document deleted')
      if (selectedDocument?.key === doc.key) {
        setSelectedDocument(null)
        setDocumentContent('')
      }
      loadDocuments()
    } catch (err) {
      setError('Failed to delete document')
    }
  }

  // Simple markdown renderer
  const renderMarkdown = (content) => {
    if (!content) return null

    const lines = content.split('\n')
    const elements = []
    let inCodeBlock = false
    let codeContent = []
    let inList = false
    let listItems = []

    const processInline = (text) => {
      // Bold
      text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      text = text.replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Code inline
      text = text.replace(/`(.+?)`/g, '<code>$1</code>')
      // Links
      text = text.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      return text
    }

    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${index}`} className="md-code-block">
              <code>{codeContent.join('\n')}</code>
            </pre>
          )
          codeContent = []
          inCodeBlock = false
        } else {
          inCodeBlock = true
        }
        return
      }

      if (inCodeBlock) {
        codeContent.push(line)
        return
      }

      // Lists
      if (line.match(/^[\-\*]\s/)) {
        if (!inList) {
          inList = true
          listItems = []
        }
        listItems.push(<li key={`li-${index}`} dangerouslySetInnerHTML={{ __html: processInline(line.substring(2)) }} />)
        return
      } else if (inList) {
        elements.push(<ul key={`ul-${index}`} className="md-list">{listItems}</ul>)
        listItems = []
        inList = false
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h3 key={index} className="md-h3">{line.substring(4)}</h3>)
        return
      }
      if (line.startsWith('## ')) {
        elements.push(<h2 key={index} className="md-h2">{line.substring(3)}</h2>)
        return
      }
      if (line.startsWith('# ')) {
        elements.push(<h1 key={index} className="md-h1">{line.substring(2)}</h1>)
        return
      }

      // Horizontal rule
      if (line.match(/^---+$/)) {
        elements.push(<hr key={index} className="md-hr" />)
        return
      }

      // Empty lines
      if (!line.trim()) {
        elements.push(<br key={index} />)
        return
      }

      // Paragraphs
      elements.push(<p key={index} className="md-p" dangerouslySetInnerHTML={{ __html: processInline(line) }} />)
    })

    // Close any open list
    if (inList && listItems.length > 0) {
      elements.push(<ul className="md-list">{listItems}</ul>)
    }

    return elements
  }

  const handleUserSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingUser) {
        await usersApi.update(editingUser.user_id, userForm)
        setSuccess(t('userUpdated', lang))
      } else {
        await usersApi.create(userForm)
        setSuccess(t('userCreated', lang))
      }
      closeUserForm()
      loadUsers()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user')
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('deleteUserConfirm', lang))) return

    try {
      await usersApi.delete(userId)
      setSuccess(t('userDeleted', lang))
      loadUsers()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user')
    }
  }

  const openEditUser = (u) => {
    setEditingUser(u)
    setUserForm({
      username: u.username,
      password: '',
      role: u.role,
      churches: u.churches || (u.church ? [u.church] : [])
    })
    setPasswordUnlocked(false)
    setShowPasswordText(false)
    setSysadminPasswordInput('')
    setPasswordUnlockError('')
    setShowUserForm(true)
  }

  const openAddUser = () => {
    resetUserForm()
    setPasswordUnlocked(true)
    setShowPasswordText(false)
    setShowUserForm(true)
  }

  const closeUserForm = () => {
    setShowUserForm(false)
    resetUserForm()
  }

  const resetUserForm = () => {
    setUserForm({
      username: '',
      password: '',
      role: 'user',
      churches: []
    })
    setEditingUser(null)
    setPasswordUnlocked(false)
    setShowPasswordText(false)
    setSysadminPasswordInput('')
    setPasswordUnlockError('')
  }

  const handleUnlockPassword = async () => {
    setPasswordUnlockError('')
    if (!sysadminPasswordInput.trim()) {
      setPasswordUnlockError(lang === 'zh' ? '請輸入系統管理員密碼' : 'Enter sysadmin password')
      return
    }
    setPasswordUnlockLoading(true)
    try {
      await usersApi.verifySysadminPassword(sysadminPasswordInput)
      setPasswordUnlocked(true)
      setSysadminPasswordInput('')
      setShowPasswordText(false)
    } catch (err) {
      setPasswordUnlockError(
        err.response?.status === 401
          ? (lang === 'zh' ? '系統管理員密碼錯誤' : 'Incorrect sysadmin password')
          : (err.response?.data?.message || (lang === 'zh' ? '驗證失敗' : 'Verification failed'))
      )
    } finally {
      setPasswordUnlockLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString()
  }

  const getRoleLabel = (role) => {
    const found = ROLES.find(r => r.value === role)
    return found ? found.label[lang] : role
  }

  const getActionLabel = (action) => {
    const actions = {
      login: { en: 'Login', zh: '登入' },
      logout: { en: 'Logout', zh: '登出' },
      user_create: { en: 'Create User', zh: '建立用戶' },
      user_update: { en: 'Update User', zh: '更新用戶' },
      user_delete: { en: 'Delete User', zh: '刪除用戶' },
      entry_save: { en: 'Save Entry', zh: '儲存條目' },
      entry_submit: { en: 'Submit Entry', zh: '提交條目' }
    }
    return actions[action]?.[lang] || action
  }

  return (
    <div className="sysadmin-page">
      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          {t('overview', lang)}
        </button>
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          {t('users', lang)}
        </button>
        <button
          className={activeTab === 'notes' ? 'active' : ''}
          onClick={() => setActiveTab('notes')}
        >
          {lang === 'zh' ? '筆記' : 'Notes'}
        </button>
        <button
          className={activeTab === 'documents' ? 'active' : ''}
          onClick={() => setActiveTab('documents')}
        >
          {lang === 'zh' ? '文件' : 'Documents'}
        </button>
        <button
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => setActiveTab('logs')}
        >
          {t('systemLogs', lang)}
        </button>
      </div>

      {/* Messages */}
      {error && <div className="message message-error">{error}</div>}
      {success && <div className="message message-success">{success}</div>}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* Maintenance Mode */}
          <div className="maintenance-section">
            <h3>{lang === 'zh' ? '系統維護' : 'System Maintenance'}</h3>
            <div className="maintenance-card">
              <div className="maintenance-info">
                <span className="maintenance-label">
                  {lang === 'zh' ? '維護模式' : 'Maintenance Mode'}
                </span>
                <span className={`maintenance-status ${maintenanceMode ? 'active' : ''}`}>
                  {maintenanceMode
                    ? (lang === 'zh' ? '已啟用' : 'Enabled')
                    : (lang === 'zh' ? '已停用' : 'Disabled')
                  }
                </span>
              </div>
              <button
                className={`btn-maintenance ${maintenanceMode ? 'btn-danger' : 'btn-primary'}`}
                onClick={toggleMaintenance}
                disabled={maintenanceLoading}
              >
                {maintenanceLoading
                  ? (lang === 'zh' ? '處理中...' : 'Processing...')
                  : maintenanceMode
                    ? (lang === 'zh' ? '停用維護模式' : 'Disable Maintenance')
                    : (lang === 'zh' ? '啟用維護模式' : 'Enable Maintenance')
                }
              </button>
            </div>
            <p className="maintenance-note">
              {lang === 'zh'
                ? '啟用後，所有頁面將顯示「系統維護中」提示訊息'
                : 'When enabled, all pages will show "System Under Maintenance" banner'
              }
            </p>
          </div>

          <div className="overview-card">
            <h3>{lang === 'zh' ? '系統概覽' : 'System Overview'}</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{users.length}</div>
                <div className="stat-label">{lang === 'zh' ? '用戶總數' : 'Total Users'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{users.filter(u => u.role === 'sysadmin').length}</div>
                <div className="stat-label">{lang === 'zh' ? '系統管理員' : 'SysAdmins'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{users.filter(u => u.role === 'admin').length}</div>
                <div className="stat-label">{lang === 'zh' ? '管理員' : 'Admins'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{users.filter(u => u.role === 'viewer').length}</div>
                <div className="stat-label">{lang === 'zh' ? '唯讀用戶' : 'View-only'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{users.filter(u => u.role === 'user').length}</div>
                <div className="stat-label">{lang === 'zh' ? '一般用戶' : 'Regular Users'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>{t('users', lang)}</h3>
            <button
              className="btn-primary"
              onClick={openAddUser}
            >
              + {t('addUser', lang)}
            </button>
          </div>

          {loading ? (
            <div className="loading">{t('loading', lang)}</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('username', lang)}</th>
                  <th>{lang === 'zh' ? '角色' : 'Role'}</th>
                  <th>{t('church', lang)}</th>
                  <th>{lang === 'zh' ? '建立時間' : 'Created'}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id}>
                    <td>{u.username}</td>
                    <td>
                      <span className={`badge badge-${u.role === 'sysadmin' ? 'danger' : u.role === 'admin' ? 'info' : u.role === 'viewer' ? 'warning' : 'success'}`}>
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td>
                      {u.churches && u.churches.length > 0
                        ? u.churches.map(c => t(c, lang)).join(', ')
                        : u.church
                          ? t(u.church, lang)
                          : '-'
                      }
                    </td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-small" onClick={() => openEditUser(u)}>
                          {t('edit', lang)}
                        </button>
                        {u.user_id !== user?.user_id && (
                          <button
                            className="btn-small btn-danger"
                            onClick={() => handleDeleteUser(u.user_id)}
                          >
                            {t('delete', lang)}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* User Form Modal */}
          {showUserForm && (
            <div className="modal-overlay" onClick={closeUserForm}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingUser ? t('editUser', lang) : t('addUser', lang)}</h3>
                  <button className="btn-close" onClick={closeUserForm}>×</button>
                </div>
                <form onSubmit={handleUserSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>{t('username', lang)} *</label>
                      <input
                        type="text"
                        value={userForm.username}
                        onChange={e => setUserForm({ ...userForm, username: e.target.value.toLowerCase() })}
                        required
                        disabled={!!editingUser}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('password', lang)} {editingUser ? `(${lang === 'zh' ? '留空表示不變更' : 'leave blank to keep'})` : '*'}</label>
                      {editingUser && !passwordUnlocked ? (
                        <div className="password-unlock-box">
                          <p className="password-unlock-hint">
                            {lang === 'zh'
                              ? '密碼已加密儲存，無法還原原文。輸入系統管理員密碼後可設定／顯示新密碼。'
                              : 'Passwords are hashed and cannot be recovered. Enter sysadmin password to set / show a new password.'}
                          </p>
                          <div className="password-unlock-row">
                            <input
                              type="password"
                              value={sysadminPasswordInput}
                              onChange={e => setSysadminPasswordInput(e.target.value)}
                              placeholder={lang === 'zh' ? '系統管理員密碼' : 'Sysadmin password'}
                              autoComplete="current-password"
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  handleUnlockPassword()
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={handleUnlockPassword}
                              disabled={passwordUnlockLoading}
                            >
                              {passwordUnlockLoading
                                ? (lang === 'zh' ? '驗證中…' : 'Verifying…')
                                : (lang === 'zh' ? '解鎖密碼' : 'Unlock')}
                            </button>
                          </div>
                          {passwordUnlockError && (
                            <div className="error-message" style={{ marginTop: '0.5rem' }}>{passwordUnlockError}</div>
                          )}
                        </div>
                      ) : (
                        <div className="password-input-row">
                          <input
                            type={showPasswordText ? 'text' : 'password'}
                            value={userForm.password}
                            onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                            required={!editingUser}
                            placeholder={editingUser ? (lang === 'zh' ? '輸入新密碼（可留空）' : 'New password (optional)') : ''}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="btn-secondary password-toggle-btn"
                            onClick={() => setShowPasswordText(v => !v)}
                          >
                            {showPasswordText
                              ? (lang === 'zh' ? '隱藏' : 'Hide')
                              : (lang === 'zh' ? '顯示' : 'Show')}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>{lang === 'zh' ? '角色' : 'Role'} *</label>
                      <select
                        value={userForm.role}
                        onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                      >
                        {ROLES.map(r => (
                          <option key={r.value} value={r.value}>{r.label[lang]}</option>
                        ))}
                      </select>
                    </div>
                    {(userForm.role === 'user' || userForm.role === 'viewer') && (
                      <div className="form-group">
                        <label>{t('churches', lang)} *</label>
                        <div className="church-checkboxes">
                          {CHURCHES.map(c => (
                            <label key={c} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={(userForm.churches || []).includes(c)}
                                onChange={() => {
                                  const current = userForm.churches || []
                                  if (current.includes(c)) {
                                    setUserForm({ ...userForm, churches: current.filter(ch => ch !== c) })
                                  } else {
                                    setUserForm({ ...userForm, churches: [...current, c] })
                                  }
                                }}
                              />
                              {t(c, lang)}
                            </label>
                          ))}
                        </div>
                        {(userForm.churches || []).length === 0 && (
                          <span className="error-text">{t('atLeastOneChurch', lang)}</span>
                        )}
                      </div>
                    )}
                    {userForm.role === 'admin' && (
                      <div className="form-group">
                        <label>{t('churches', lang)} ({lang === 'zh' ? '可選' : 'Optional'})</label>
                        <div className="church-checkboxes">
                          {CHURCHES.map(c => (
                            <label key={c} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={(userForm.churches || []).includes(c)}
                                onChange={() => {
                                  const current = userForm.churches || []
                                  if (current.includes(c)) {
                                    setUserForm({ ...userForm, churches: current.filter(ch => ch !== c) })
                                  } else {
                                    setUserForm({ ...userForm, churches: [...current, c] })
                                  }
                                }}
                              />
                              {t(c, lang)}
                            </label>
                          ))}
                        </div>
                        <span className="hint-text">{lang === 'zh' ? '不選擇則可管理所有堂會' : 'Leave empty to manage all churches'}</span>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={closeUserForm}>
                      {t('cancel', lang)}
                    </button>
                    <button type="submit" className="btn-primary">
                      {t('save', lang)}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="tab-content">
          {/* Category Sub-tabs */}
          <div className="sub-tabs">
            <button
              className={noteCategory === 'feature' ? 'active' : ''}
              onClick={() => setNoteCategory('feature')}
            >
              {lang === 'zh' ? '功能修改' : 'Feature Changes'}
            </button>
            <button
              className={noteCategory === 'note' ? 'active' : ''}
              onClick={() => setNoteCategory('note')}
            >
              {lang === 'zh' ? '筆記' : 'Notes'}
            </button>
            <button
              className={noteCategory === 'vibe' ? 'active' : ''}
              onClick={() => setNoteCategory('vibe')}
            >
              Vibe心得
            </button>
            <button
              className={noteCategory === 'milestone' ? 'active' : ''}
              onClick={() => setNoteCategory('milestone')}
            >
              {lang === 'zh' ? '里程碑' : 'Milestones'}
            </button>
          </div>

          <div className="tab-header">
            <h3>
              {noteCategory === 'feature' && (lang === 'zh' ? '功能修改' : 'Feature Changes')}
              {noteCategory === 'note' && (lang === 'zh' ? '筆記' : 'Notes')}
              {noteCategory === 'vibe' && 'Vibe心得'}
              {noteCategory === 'milestone' && (lang === 'zh' ? '里程碑' : 'Milestones')}
            </h3>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingNote(null)
                setNoteForm({ title: '', description: '' })
                setShowNoteForm(true)
              }}
            >
              + {lang === 'zh' ? '新增' : 'Add'}
            </button>
          </div>

          {loading ? (
            <div className="loading">{t('loading', lang)}</div>
          ) : notes.length === 0 ? (
            <div className="empty-state">
              {lang === 'zh' ? '尚無筆記' : 'No notes yet'}
            </div>
          ) : (
            <div className="notes-list">
              {notes.map(note => (
                <div key={note.note_id} className={`note-card ${note.completed ? 'completed' : ''}`}>
                  <div className="note-header">
                    <input
                      type="checkbox"
                      checked={note.completed}
                      onChange={() => toggleNoteComplete(note)}
                      className="note-checkbox"
                    />
                    <span className="note-title">{note.title}</span>
                    <div className="note-actions">
                      <button className="btn-small" onClick={() => openEditNote(note)}>
                        {t('edit', lang)}
                      </button>
                      <button className="btn-small btn-danger" onClick={() => handleDeleteNote(note.note_id)}>
                        {t('delete', lang)}
                      </button>
                    </div>
                  </div>
                  {note.description && (
                    <div className="note-description">{note.description}</div>
                  )}
                  <div className="note-meta">
                    <span>{lang === 'zh' ? '建立於' : 'Created'}: {formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Note Form Modal */}
          {showNoteForm && (
            <div className="modal-overlay" onClick={() => setShowNoteForm(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingNote ? (lang === 'zh' ? '編輯筆記' : 'Edit Note') : (lang === 'zh' ? '新增筆記' : 'Add Note')}</h3>
                  <button className="btn-close" onClick={() => setShowNoteForm(false)}>×</button>
                </div>
                <form onSubmit={handleNoteSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>{lang === 'zh' ? '標題' : 'Title'} *</label>
                      <input
                        type="text"
                        value={noteForm.title}
                        onChange={e => setNoteForm({ ...noteForm, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{lang === 'zh' ? '描述' : 'Description'}</label>
                      <textarea
                        value={noteForm.description}
                        onChange={e => setNoteForm({ ...noteForm, description: e.target.value })}
                        rows={4}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={() => setShowNoteForm(false)}>
                      {t('cancel', lang)}
                    </button>
                    <button type="submit" className="btn-primary">
                      {t('save', lang)}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>{t('systemLogs', lang)}</h3>
            <div className="filters">
              <input
                type="text"
                placeholder={lang === 'zh' ? '用戶 ID' : 'User ID'}
                value={logFilters.userId}
                onChange={e => setLogFilters({ ...logFilters, userId: e.target.value })}
              />
              <select
                value={logFilters.action}
                onChange={e => setLogFilters({ ...logFilters, action: e.target.value })}
              >
                <option value="">{lang === 'zh' ? '所有操作' : 'All Actions'}</option>
                <option value="login">{lang === 'zh' ? '登入' : 'Login'}</option>
                <option value="logout">{lang === 'zh' ? '登出' : 'Logout'}</option>
                <option value="user_create">{lang === 'zh' ? '建立用戶' : 'Create User'}</option>
                <option value="user_update">{lang === 'zh' ? '更新用戶' : 'Update User'}</option>
                <option value="user_delete">{lang === 'zh' ? '刪除用戶' : 'Delete User'}</option>
              </select>
              <button className="btn-secondary" onClick={loadLogs}>
                {t('search', lang)}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">{t('loading', lang)}</div>
          ) : (
            <table className="logs-table">
              <thead>
                <tr>
                  <th>{t('timestamp', lang)}</th>
                  <th>{lang === 'zh' ? '用戶' : 'User'}</th>
                  <th>{lang === 'zh' ? '操作' : 'Action'}</th>
                  <th>{lang === 'zh' ? '目標' : 'Target'}</th>
                  <th>{t('details', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.log_id}>
                    <td>{formatDate(log.timestamp)}</td>
                    <td>{log.user_id}</td>
                    <td>{getActionLabel(log.action)}</td>
                    <td>{log.target_id || '-'}</td>
                    <td>
                      {log.details && (
                        <pre className="log-details">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}


      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="tab-content">
          <div className="tab-header">
            <h3>{lang === 'zh' ? '文件' : 'Documents'}</h3>
            <button className="btn-primary" onClick={() => setShowUploadModal(true)}>
              + {lang === 'zh' ? '上傳' : 'Upload'}
            </button>
          </div>

          <div className="documents-container">
            {/* Document List */}
            <div className="document-list">
              {loading ? (
                <div className="loading">{t('loading', lang)}</div>
              ) : documents.length === 0 ? (
                <div className="empty-state">
                  {lang === 'zh' ? '尚無文件' : 'No documents yet'}
                </div>
              ) : (
                documents.map(doc => (
                  <div
                    key={doc.key}
                    className={`document-item ${selectedDocument?.key === doc.key ? 'active' : ''}`}
                    onClick={() => handleViewDocument(doc)}
                  >
                    <div className="document-name">{doc.filename}</div>
                    <div className="document-meta">
                      <span>{(doc.size / 1024).toFixed(1)} KB</span>
                      <span>{formatDate(doc.lastModified)}</span>
                    </div>
                    <button
                      className="btn-small btn-danger"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc); }}
                    >
                      {t('delete', lang)}
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Document Viewer */}
            <div className="document-viewer">
              {selectedDocument ? (
                <div className="markdown-content">
                  <div className="viewer-header">
                    <h4>{selectedDocument.filename}</h4>
                    <button className="btn-secondary" onClick={() => { setSelectedDocument(null); setDocumentContent(''); }}>
                      {lang === 'zh' ? '關閉' : 'Close'}
                    </button>
                  </div>
                  <div className="viewer-body">
                    {renderMarkdown(documentContent)}
                  </div>
                </div>
              ) : (
                <div className="empty-viewer">
                  {lang === 'zh' ? '選擇一個文件來查看' : 'Select a document to view'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{lang === 'zh' ? '上傳 Markdown 文件' : 'Upload Markdown Files'}</h3>
              <button className="btn-close" onClick={() => { setShowUploadModal(false); setUploadFiles([]); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{lang === 'zh' ? '選擇 .md 文件（可多選）' : 'Select .md files (multiple allowed)'}</label>
                <input
                  type="file"
                  accept=".md"
                  multiple
                  onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                />
              </div>
              {uploadFiles.length > 0 && (
                <div className="file-list">
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} className="file-item">
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  ))}
                </div>
              )}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: uploadProgress + '%' }}></div>
                  <span>{uploadProgress}%</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowUploadModal(false); setUploadFiles([]); }}>
                {t('cancel', lang)}
              </button>
              <button
                className="btn-primary"
                onClick={handleFileUpload}
                disabled={uploadFiles.length === 0 || loading}
              >
                {loading ? (lang === 'zh' ? '上傳中...' : 'Uploading...') : t('submit', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .sysadmin-page {
          min-height: 60vh;
        }

        .tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 12px;
        }

        .tabs button {
          padding: 10px 20px;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          border-bottom: 2px solid transparent;
        }

        .tabs button.active {
          color: var(--accent-primary);
          border-bottom-color: var(--accent-primary);
        }

        .tabs button:hover {
          color: var(--text-primary);
        }

        .tab-content {
          margin-top: 24px;
        }

        .sub-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .sub-tabs button {
          padding: 8px 16px;
          border: 1px solid var(--border-color);
          background: var(--bg-card);
          color: var(--text-primary);
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: all 0.2s;
        }

        .sub-tabs button:hover {
          background: var(--bg-hover);
        }

        .sub-tabs button.active {
          background: var(--accent-primary);
          color: white;
          border-color: var(--accent-primary);
        }

        .tab-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .tab-header h3 {
          margin: 0;
        }

        .sub-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .sub-tabs button {
          padding: 8px 16px;
          border: 1px solid var(--border-color);
          background: var(--bg-card);
          color: var(--text-secondary);
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: all 0.2s;
        }

        .sub-tabs button:hover {
          background: var(--bg-hover);
        }

        .sub-tabs button.active {
          background: var(--accent-primary);
          color: white;
          border-color: var(--accent-primary);
        }

        /* Maintenance Section */
        .maintenance-section {
          margin-bottom: 24px;
        }

        .maintenance-section h3 {
          margin-bottom: 16px;
        }

        .maintenance-card {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        /* Documents styles */
        .documents-container {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 24px;
          min-height: 500px;
        }

        .document-list {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          overflow-y: auto;
          max-height: 600px;
        }

        .document-item {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);
          cursor: pointer;
          transition: background 0.2s;
        }

        .document-item:hover {
          background: var(--bg-hover);
        }

        .document-item.active {
          background: var(--accent-primary);
          color: white;
        }

        .document-item.active .document-meta {
          color: rgba(255, 255, 255, 0.8);
        }

        .document-name {
          font-weight: 500;
          margin-bottom: 4px;
          word-break: break-all;
        }

        .document-meta {
          display: flex;
          gap: 12px;
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .document-viewer {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          padding: 24px;
          overflow-y: auto;
          max-height: 600px;
        }

        .empty-viewer {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--text-muted);
        }

        .viewer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-color);
        }

        .viewer-header h4 {
          margin: 0;
        }

        .viewer-body {
          line-height: 1.6;
        }

        /* Markdown styles */
        .markdown-content .md-h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin: 1.5rem 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--border-color);
        }

        .markdown-content .md-h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.25rem 0 0.75rem;
          color: var(--text-primary);
        }

        .markdown-content .md-h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
          color: var(--text-primary);
        }

        .markdown-content .md-h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem;
        }

        .markdown-content .md-paragraph {
          margin: 0.5rem 0;
        }

        .markdown-content .md-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .markdown-content .md-list li {
          margin: 0.25rem 0;
        }

        .markdown-content .md-code-block {
          background: var(--bg-secondary);
          padding: 16px;
          border-radius: var(--border-radius);
          overflow-x: auto;
          font-family: monospace;
          font-size: 0.875rem;
          margin: 1rem 0;
        }

        .markdown-content .md-code-block code {
          background: transparent;
          color: var(--text-primary);
        }

        .markdown-content .md-inline-code {
          background: var(--bg-secondary);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.875em;
        }

        .markdown-content strong {
          font-weight: 600;
        }

        .markdown-content em {
          font-style: italic;
        }

        .markdown-content a {
          color: var(--accent-primary);
          text-decoration: none;
        }

        .markdown-content a:hover {
          text-decoration: underline;
        }

        .file-info {
          margin-top: 8px;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .file-list {
          margin-top: 12px;
          max-height: 200px;
          overflow-y: auto;
        }

        .file-item {
          padding: 8px 12px;
          background: var(--bg-secondary);
          border-radius: 4px;
          margin-bottom: 4px;
          font-size: 0.875rem;
        }

        .upload-progress {
          margin-top: 12px;
          position: relative;
          height: 24px;
          background: var(--bg-secondary);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: var(--accent-primary);
          transition: width 0.3s;
        }

        .upload-progress span {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .file-list {
          margin-top: 12px;
          max-height: 200px;
          overflow-y: auto;
        }

        .file-item {
          padding: 8px 12px;
          background: var(--bg-secondary);
          border-radius: 4px;
          margin-bottom: 4px;
          font-size: 0.875rem;
        }

        .upload-progress {
          margin-top: 12px;
          position: relative;
          height: 24px;
          background: var(--bg-secondary);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: var(--accent-primary);
          transition: width 0.3s;
        }

        .upload-progress span {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .maintenance-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .maintenance-label {
          font-weight: 600;
        }

        .maintenance-status {
          font-size: 0.875rem;
          padding: 4px 12px;
          border-radius: 4px;
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .maintenance-status.active {
          background: #fef2f2;
          color: #dc2626;
        }

        .btn-maintenance {
          padding: 12px 24px;
          border: none;
          border-radius: var(--border-radius);
          color: white;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .btn-maintenance:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-maintenance.btn-primary {
          background: var(--accent-primary);
        }

        .btn-maintenance.btn-primary:hover:not(:disabled) {
          background: var(--accent-primary);
        }

        .btn-maintenance.btn-danger {
          background: var(--accent-danger);
        }

        .btn-maintenance.btn-danger:hover:not(:disabled) {
          background: #b91c1c;
        }

        .maintenance-note {
          margin-top: 8px;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        /* Notes Section */
        .notes-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .note-card {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          padding: 16px;
          border-left: 4px solid var(--accent-primary);
        }

        .note-card.completed {
          border-left-color: var(--accent-success);
          opacity: 0.7;
        }

        .note-card.completed .note-title {
          text-decoration: line-through;
        }

        .note-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .note-checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .note-title {
          flex: 1;
          font-weight: 600;
        }

        .note-actions {
          display: flex;
          gap: 8px;
        }

        .note-description {
          margin-top: 8px;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .note-meta {
          margin-top: 8px;
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .empty-state {
          text-align: center;
          padding: 48px;
          color: var(--text-secondary);
        }

        /* Stats Grid */
        .overview-card {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          padding: 24px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-top: 16px;
        }

        .stat-card {
          background: var(--bg-secondary);
          border-radius: var(--border-radius);
          padding: 20px;
          text-align: center;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 600;
          color: var(--accent-primary);
        }

        .stat-label {
          color: var(--text-secondary);
          margin-top: 8px;
        }

        /* Tables */
        .users-table,
        .logs-table {
          width: 100%;
          border-collapse: collapse;
        }

        .users-table th,
        .users-table td,
        .logs-table th,
        .logs-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }

        .users-table th,
        .logs-table th {
          background: var(--bg-secondary);
          color: var(--text-secondary);
          font-weight: 600;
        }

        .users-table tr:hover,
        .logs-table tr:hover {
          background: var(--bg-hover);
        }

        .actions {
          display: flex;
          gap: 8px;
        }

        .btn-small {
          padding: 6px 12px;
          background: var(--accent-primary);
          border: none;
          border-radius: 4px;
          color: white;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-small:hover {
          background: var(--accent-primary);
        }

        .btn-small.btn-danger {
          background: var(--accent-danger);
        }

        .btn-small.btn-danger:hover {
          background: #dc2626;
        }

        .btn-primary {
          padding: 10px 20px;
          background: var(--accent-primary);
          border: none;
          border-radius: var(--border-radius);
          color: white;
          cursor: pointer;
          font-weight: 600;
        }

        .btn-primary:hover {
          background: var(--accent-primary);
        }

        .btn-secondary {
          padding: 10px 20px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          color: var(--text-primary);
          cursor: pointer;
        }

        .btn-secondary:hover {
          background: var(--bg-hover);
        }

        /* Filters */
        .filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filters input,
        .filters select {
          width: auto;
          min-width: 150px;
        }

        /* Log Details */
        .log-details {
          background: var(--bg-secondary);
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 0.75rem;
          overflow-x: auto;
          margin: 0;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-color);
        }

        .modal-header h3 {
          margin: 0;
        }

        .btn-close {
          background: none;
          border: none;
          color: var(--text-secondary);
          font-size: 24px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-color);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          padding: 10px 12px;
          border: 2px solid var(--input-border, var(--border-color));
          background: var(--bg-input);
          border-radius: var(--border-radius);
          font-size: 1rem;
        }

        .form-group textarea {
          resize: vertical;
        }

        .password-unlock-box {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 12px;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
          background: var(--bg-secondary, rgba(0, 0, 0, 0.03));
        }

        .password-unlock-hint {
          margin: 0;
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.4;
        }

        .password-unlock-row,
        .password-input-row {
          display: flex;
          gap: 8px;
          align-items: stretch;
        }

        .password-unlock-row input,
        .password-input-row input {
          flex: 1;
          min-width: 0;
        }

        .password-toggle-btn {
          white-space: nowrap;
          flex-shrink: 0;
        }

        .church-checkboxes {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }

        .error-text {
          color: var(--accent-danger);
          font-size: 0.875rem;
        }

        .hint-text {
          color: var(--text-muted);
          font-size: 0.875rem;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .tab-header {
            flex-direction: column;
            gap: 12px;
          }

          .filters {
            flex-direction: column;
            width: 100%;
          }

          .filters input,
          .filters select,
          .filters button {
            width: 100%;
          }

          .maintenance-card {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  )
}